import { DataView } from 'components/DataView/DataView';
import { DTOgetOrderDetailedData } from 'types/orders';
import { adapterPaymentDataFromAPI, adapterServiceLogisticPaymentDataFromAPI } from './adapters';
import { PAYMENT_STATUS_COLORS, PAYMENT_STATUS_LABELS } from './constants';
import { CommittedPaymentEditor } from './components/CommittedPaymentEditor/CommittedPaymentEditor';
import { PaymentVoucherUploader } from './components/PaymentVoucherUploader/PaymentVoucherUploader';
import { getUIStateForLogisticServiceView, getUIStateForOrderView } from './utils';
import { PaymentLinkRegister } from './components/PaymentLinkRegister/PaymentLinkRegister';
import CreditDaysCounter from 'components/CreditDaysCounter/CreditDaysCounter';
import { ViewPaymentDataButton } from 'views/orders/OrderDetailInternalCard/Components/ViewPaymentDataButton/ViewPaymentDataButton';
import { IlogisticServiceDetails } from 'types/services';
import { useMemo } from 'react';

import './BillingPaymentStatusSection.scss';

interface IBillingPaymentStatusSection {
  orderData?: DTOgetOrderDetailedData;
  logisticServiceData?: IlogisticServiceDetails;
  refreshData?: () => void;
}

export const BillingPaymentStatusSection = ({
  orderData,
  logisticServiceData,
  refreshData
}: IBillingPaymentStatusSection) => {
  let id = orderData?.orderId ?? logisticServiceData?.id;
  id = id ? Number(id) : undefined;

  const creditDaysText = 'Servicio confirmado automáticamente con acuerdo de pago de';

  const payment = useMemo(() => {
    return logisticServiceData
      ? adapterServiceLogisticPaymentDataFromAPI(logisticServiceData)
      : adapterPaymentDataFromAPI(orderData);
  }, [logisticServiceData, orderData]);

  const uiState = useMemo(() => {
    return logisticServiceData
      ? getUIStateForLogisticServiceView(logisticServiceData)
      : getUIStateForOrderView(orderData);
  }, [logisticServiceData, orderData]);

  const {
    showStatus,
    showCommittedPaymentDate,
    showPaymentAgreementAlert,
    showPaymentLink,
    showPaymentDataButton,
    showPaymentUploader,
    disablePaymentUploader
  } = uiState ?? {};

  const cells = [
    {
      id: 'status',
      title: 'Estado',
      hidden: !showStatus,
      style: {
        flex: 0,
        minWidth: 130
      },
      renderValue: () => (
        <div className="statusCell">
          {payment?.status ? (
            <>
              <div className={`statusDot ${PAYMENT_STATUS_COLORS[payment?.status!]?.backgroundColor ?? ''}`}></div>
              <p className={`statusText ${PAYMENT_STATUS_COLORS[payment?.status!]?.color ?? ''} `}>
                {PAYMENT_STATUS_LABELS[payment?.status!]}
              </p>
            </>
          ) : null}
        </div>
      )
    },
    {
      id: 'payment',
      title: 'Método',
      renderValue: () => <p className="paymentText">{payment?.method}</p>
    },
    {
      id: 'committedPaymentDate',
      title: 'Acuerdo de pago',
      hidden: !showCommittedPaymentDate,
      renderValue: () => (
        <div>
          <CommittedPaymentEditor
            id={id as number}
            logisticService={Boolean(logisticServiceData)}
            committedPaymentDate={payment?.committedPaymentDate!}
            onChange={() => refreshData?.()}
          />
        </div>
      )
    },
    {
      id: 'tooltip',
      hidden: !showPaymentDataButton,
      renderCell: () => (
        <div className="tooltipContainer">
          <div>
            <ViewPaymentDataButton paymentData={payment?.paymentData} />
          </div>
        </div>
      )
    },
    {
      id: 'paymentLink',
      hidden: !showPaymentLink,
      style: {
        flex: 0,
        minWidth: 260
      },
      renderCell: () => (
        <PaymentLinkRegister
          id={id as number}
          logisticService={Boolean(logisticServiceData)}
          defaultLink={payment?.paymentLink!}
        />
      )
    }
  ];

  return (
    <div className="billingPaymentStatusSection">
      <p className="billingPaymentStatusSectionTitle">Información de pago</p>
      <div
        className={`billingPaymentStatusSectionBorder ${PAYMENT_STATUS_COLORS[payment?.status!]?.borderColor ?? ''}`}
      >
        <div className="billingPaymentStatusSectionBorderMain with32pxGap">
          <div className="container">
            <DataView cells={cells} className="with32pxGap centerDataView" data={{ payment }} />
            {showPaymentAgreementAlert && (
              <div>
                <CreditDaysCounter
                  customText={logisticServiceData ? creditDaysText : undefined}
                  customClassCreditDaysCounter="!w-full"
                  showCreditDaysCounter={showPaymentAgreementAlert}
                  days={payment?.paymentAgreementDays!}
                />
              </div>
            )}
          </div>
          {showPaymentUploader && (
            <div className="paymentVoucherUploaderContainer">
              <PaymentVoucherUploader
                id={id as number}
                billingCountryId={logisticServiceData?.billingCountry?.id}
                logisticService={Boolean(logisticServiceData)}
                disabled={disablePaymentUploader}
                voucherUrl={payment?.voucherUrl}
                onUpload={refreshData}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
